import { observer } from "mobx-react";
import { ReactiveContentItem } from "../../schoolfeed/SchoolFeedContentItem";
import React, { RefObject } from "react";
import styles from "./styles/NewsModal.module.scss";
import FontAwesome from "../../utilities/FontAwesome";
import { withoutLocaleInURI } from "../../../internationalization/i18nURI";
import { useStores } from "../../../stores/useStores";
import { TranslatedText } from "../../utilities/TranslatedText";
import { Button, Flex } from "@radix-ui/themes";

type ContentNode = Element | Text | null | undefined;

const NewsModalContentHeader = observer(
  (props: {
    index: number;
    itemObj: ReactiveContentItem | IAccordionItem;
    navigationButtons?: React.ReactElement;
    closeFunction?: () => void;
    printRef: RefObject<ContentNode> | undefined;
    shareUrlPath: string;
  }) => {
    const { modalStore, organizationStore } = useStores();

    async function onClick() {
      modalStore.addModal({
        type: "shareContentItem",
        url: withoutLocaleInURI(
          "https://" + window.location.host + props.shareUrlPath,
        ),
        title: props.itemObj.title,
      });
    }

    const printContent = () => {
      // Open a new tab
      const printWindow = window.open("", "_blank");
      const title = organizationStore.organization.title;
      const mediaUrl =
        organizationStore.organization.favicon ||
        organizationStore.organization.top_nav_logo;

      if (!printWindow || !props?.printRef?.current) {
        console.error("Print reference is null or window could not be opened");
        return;
      }

      // Clone the content to print
      const contentToPrint = props.printRef.current.cloneNode(true);

      const titleTag = document.head.querySelector("title");
      if (titleTag) {
        titleTag.textContent = `${props.itemObj.title} - ${organizationStore.organization.title}`;
      }

      // Copy the entire head content to include styles
      const headContent = document.head.innerHTML;

      // Write full HTML into the new window
      printWindow.document.open();
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
            <head>
                ${headContent}
                <style>
                    * {
                        box-sizing: border-box;
                    }
                    #watermark {
                        position: fixed;
                        bottom: 16px;
                        right: 16px;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        pointer-events: none;
                        z-index: 9999;
                    }
                    #watermark img {
                        width: 20px;
                        height: 20px;
                    }
                    #watermark span {
                        font-size: 12px;
                        font-weight: 600;
                        white-space: nowrap;
                        color: black;
                    }
                    html, body {
                        margin: 0;
                        padding: 0;
                        width: 100%;
                        height: 100%;
                    }
                    #print-container {
                        padding-top: 20px;
                    }
                    @media print {
                        body {
                            -webkit-print-color-adjust: exact;
                        }
                    }
                </style>
            </head>
            <body>
            <div id="watermark">
                <img src="${mediaUrl}" alt="" />
                <span>${title}</span>
            </div>
                <div id="print-container"></div>
            </body>
        </html>
    `);
      printWindow.document.close();

      // Wait for the new window's content to be ready
      const interval = setInterval(() => {
        if (printWindow.document.readyState === "complete") {
          clearInterval(interval);
          const container =
            printWindow.document.getElementById("print-container");
          if (container && contentToPrint) {
            container.appendChild(contentToPrint);
          }

          // Delay slightly to ensure render before printing
          setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            // close window after cancelling
            setTimeout(() => printWindow.close(), 500);
          }, 300);
        }
      }, 100);
    };

    return (
      <header>
        <Button
          variant="ghost"
          highContrast
          radius="full"
          size="3"
          onClick={() => {
            if (props.closeFunction) {
              props.closeFunction();
            } else {
              modalStore.removeTop();
            }
          }}
        >
          <FontAwesome ariaHidden name={"fa-solid fa-times"} />
          <TranslatedText className={styles.buttonLabel}>Close</TranslatedText>
        </Button>
        <Flex align="center" gap="4">
          {props.navigationButtons}
          <Flex align="center" gap="2">
            <Button
              variant="outline"
              highContrast
              radius="full"
              size="3"
              onClick={onClick}
            >
              <FontAwesome
                name={"fa-external-link-alt"}
                prefix={"fas"}
                ariaHidden
              />
              <TranslatedText className={styles.buttonLabel}>
                Share
              </TranslatedText>
            </Button>
            <Button
              variant="outline"
              highContrast
              radius="full"
              size="3"
              onClick={printContent}
            >
              <FontAwesome name={"fa-print"} prefix={"fas"} ariaHidden />
              <TranslatedText className={styles.buttonLabel}>
                Print
              </TranslatedText>
            </Button>
          </Flex>
        </Flex>
      </header>
    );
  },
);

export default NewsModalContentHeader;
